<script setup lang="ts">
import useNotyf from '/@src/composable/useNotyf'
import useNotification from '/@src/composable/message/useNotification'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { timeFormat } from '/@src/utils/date'
import { toRefs, useMediaQuery } from '@vueuse/core'
import { onMounted, PropType, ref, watch } from 'vue'
import { ContentKind, NotificationParams } from '/@src/models/api'
import {
  Roadshow,
  Video,
  Content,
  Sku,
} from '/@src/__generated__/generated-gql-types'
import { ProductContent } from '/@src/models/content'
import { useReportUtils } from '/@src/composable/useReportUtils'

const route = useRoute()

export type NotificationType =
  | 'omenReport'
  | 'article'
  | 'video'
  | 'roadshow'
  | 'member-page'
  | 'product'
  | 'customize'
  | 'report'
  | 'post'
  | 'transcript'

export interface NotificationFormData {
  id: number
  title: string
  content: string
  date?: Date
  url?: string
}

export interface OmniNotificationFormParams {
  key: string
  name: string
  value: {
    title: string
    body: string
    url: string
    publishedAt: string
  }
}

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  titleLimit: {
    type: Number,
    default: 80,
  },
  content: {
    type: String,
    default: '',
  },
  showNotificationCheck: {
    type: Boolean,
    default: true,
  },
  initType: {
    type: String as PropType<NotificationType>,
    default: 'article',
  },
  pushType: {
    type: String as PropType<NotificationType>,
    default: '',
  },
  /**
   * 是否可选择推送类型
   */
  showTypeOption: {
    type: Boolean,
    default: false,
  },
})

const { t } = useI18n()
const notify = useNotyf()
const dateError = ref(false)
const propsData = toRefs(props)
const isNotificationCheck = ref(false)
const { getReportTitle } = useReportUtils()
const { sendNotificationData, settingOmniNotificationKvData } =
  useNotification()
const notificationForm = ref<NotificationFormData>({
  id: -1,
  title: props.title,
  content: props.content,
  date: undefined,
  url: undefined,
})
const isMobileScreen = useMediaQuery('(max-width: 767px)')
const notificationType = ref<NotificationType>('member-page')

/**
 * 推送内容选项
 */
const optionsPushType = [
  {
    value: 'omenReport',
    label: t('form-field-omen-report'),
  },
  {
    value: 'member-page',
    label: t('page-title-member'),
  },
  {
    value: 'transcript',
    label: t('content-type-transcript')
  },
  {
    value: 'article',
    label: t('content-type-article'),
  },
  {
    value: 'video',
    label: t('content-type-video'),
  },
  {
    value: 'roadshow',
    label: t('content-type-roadshow'),
  },
  {
    value: 'product',
    label: t('title-products-panel'),
  },
  {
    value: 'customize',
    label: t('title-customize'),
  },
]

const optionsPlatformType = [
  {
    label: '全部',
    value: 'all',
  },
  {
    label: '安卓',
    value: 'android',
  },
  {
    label: '苹果',
    value: 'ios',
  },
]

onMounted(() => {
  if (!props.showNotificationCheck) {
    isNotificationCheck.value = true
  }
  notificationForm.value.url = makeNotificationUrl()
})

watch(propsData.title, () => {
  notificationForm.value.title = propsData.title.value
  if (!notificationForm.value.title) {
    notificationForm.value.title = ''
  }
})

watch(propsData.content, () => {
  notificationForm.value.content = propsData.content.value
  if (!notificationForm.value.content) {
    notificationForm.value.content = ''
  }
})

watch(isNotificationCheck, (value) => {
  if (value && route.params.id) {
    // 适配路径id参数
    notificationForm.value.url = makeNotificationUrl(
      new Map([['id', parseInt(route.params.id as string)]])
    )
  }
})

watch(propsData.pushType, (type) => {
  if (type === 'customize') {
    notificationType.value = 'customize'
  }
})

const isNotificationChecked = (): boolean => {
  return isNotificationCheck.value
}

const setNotificationChecked = (value: boolean): void => {
  isNotificationCheck.value = value
}

/**
 * 验证推送数据是否正确设置
 */
const isNotificationValid = (): boolean => {
  console.log('call isNotificationValid')
  if (isNotificationCheck.value) {
    if (notificationForm.value.title.length === 0) {
      notify.error(t('error-notification-title-empty'))
      return false
    }
    if (notificationForm.value.title.length > props.titleLimit) {
      notify.error(t('error-notification-title-limit'))
      return false
    }
    return true
  } else {
    return true
  }
}

const makeNotificationUrl = (
  params: Map<string, number | Array<number>> = new Map()
): string => {
  let url: string = ''
  let type: NotificationType
  if (props.showTypeOption) {
    type = notificationType.value
  } else if (propsData.pushType.value) {
    type = propsData.pushType.value
  } else {
    type = props.initType
  }
  const id = params.get('id')
  switch (type) {
    case 'omenReport':
    case 'article':
    case 'video':
    case 'roadshow':
    case 'post':
    case 'transcript':
    case 'product':
      if (!id) {
        return ''
      }
  }
  const omen = import.meta.env.VITE_OMEN_HOST_URL as string
  const origin = import.meta.env.VITE_WEB_HOST_URL as string

  switch (type) {
    case 'omenReport':
      url = `${omen}/documents/${id}?kind=${ContentKind.REPORT}`
      break
    case 'transcript': 
      url = `${omen}/transcripts/${id}`
      break
    case 'article':
      url = `${origin}/articles/${id}`
      break
    case 'video':
      url = `${origin}/videos/${id}`
      break
    case 'roadshow':
      url = `${origin}/timeline?tab=roadshow&id=${id}&kind=${ContentKind.ROADSHOW}`
      break
    case 'member-page':
      url = `${origin}/member`
      break
    case 'product':
      const ids = id!.toString().split(',')
      url = `${origin}/product/${ids[0]}`
      break
    case 'post':
      url = `${origin}/content/posts/${id}`
      break
  }
  return url
}

const sendNotification = async () => {
  let pushDate = undefined
  if (notificationForm.value.date) {
    pushDate = timeFormat(
      notificationForm.value.date,
      'YYYY-MM-DD HH:mm:ss',
      true
    )
  }
  const params: NotificationParams = {
    title: notificationForm.value.title,
    body: notificationForm.value.content,
    start_time: pushDate,
    extra: {
      url: notificationForm.value.url,
    },
    platform: notificationForm.value.platform,
  }
  return sendNotificationData(params)
}

const sendOmniNotificationKv = async () => {
  const date = notificationForm.value.date
  const dt = date ? new Date(date) : new Date()
  const pushDate = dt.toISOString().replace('Z', '+08:00')
  const params: OmniNotificationFormParams = {
    key: 'omni-notification',
    name: 'notification',
    value: {
      title: notificationForm.value.title,
      body: notificationForm.value.content,
      url: notificationForm.value.url ?? '',
      publishedAt: pushDate,
    },
  }
  return settingOmniNotificationKvData(params)
}

const handleChangeType = (type: NotificationType) => {
  notificationType.value = type
  switch (type) {
    case 'member-page':
      notificationForm.value.url = makeNotificationUrl()
      break
    default:
      notificationForm.value.url = ''
      break
  }
}

const setNotificationData = (params: NotificationFormData) => {
  notificationForm.value.id = params.id
  notificationForm.value.title = params.title
  notificationForm.value.content = params.content
  notificationForm.value.url = makeNotificationUrl(new Map([['id', params.id]]))
}

const reset = () => {
  notificationForm.value.date = undefined
  notificationForm.value.title = ''
  notificationForm.value.content = ''
}

const handleProductSelect = (product: ProductContent[]) => {
  let params = new Map([['id', `${product[0].commodity_id},${product[0].id}`]])
  product.map((x) => {
    notificationForm.value.title = x.name as string
  })
  notificationForm.value.url = makeNotificationUrl(params)
}

const handleArticleSelect = (article: Content[]) => {
  notificationForm.value.url = makeNotificationUrl(
    new Map([['id', article.map((x) => x.raw_id!)]])
  )
  article.map((x) => {
    notificationForm.value.title = x.title as string
    notificationForm.value.content = x.description as string
    if (new Date(x.datetime) < new Date()) {
      dateError.value = true
      notify.error(t('error-notification-date'))
    } else {
      dateError.value = false
      notificationForm.value.date = x.datetime
    }
  })
}

const handleOmenReportSelect = (report: Content[]) => {
  notificationForm.value.url = makeNotificationUrl(
    new Map([['id', report.map((x) => x.raw_id!)]])
  )
  report.map((x) => {
    notificationForm.value.title = getReportTitle(x) as string
    notificationForm.value.content = x.description as string
    if (new Date(x.datetime) < new Date()) {
      dateError.value = true
      notify.error(t('error-notification-date'))
    } else {
      dateError.value = false
      notificationForm.value.date = x.datetime
    }
  })
}

const handleVideoSelect = (video: Content[]) => {
  notificationForm.value.url = makeNotificationUrl(
    new Map([['id', video.map((x) => x.raw_id!)]])
  )
  video.map((x) => {
    notificationForm.value.title = x.title as string
    notificationForm.value.content = x.description as string
    if (new Date(x.datetime) < new Date()) {
      dateError.value = true
      notify.error(t('error-notification-date'))
    } else {
      dateError.value = false
      notificationForm.value.date = x.datetime
    }
  })
}

const handleRoadshowSelect = (roadshow: Roadshow[]) => {
  notificationForm.value.url = makeNotificationUrl(
    new Map([['id', roadshow.map((x) => x.id!)]])
  )
  roadshow.map((x) => {
    notificationForm.value.title = x.title as string
    notificationForm.value.content = x.summary as string
    if (new Date(x.created_at) < new Date()) {
      dateError.value = true
      notify.error(t('error-notification-date'))
    } else {
      dateError.value = false
      notificationForm.value.date = x.created_at
    }
  })
}

const handlePostSelect = (report: Content[]) => {
  notificationForm.value.url = makeNotificationUrl(
    new Map([['id', report.map((x) => x.raw_id!)]])
  )
  report.map((x) => {
    notificationForm.value.title = `【${x.author?.nickname||'智堡'}】发布了一条动态`
    notificationForm.value.content = x.description as string
    if (new Date(x.datetime) < new Date()) {
      dateError.value = true
      notify.error(t('error-notification-date'))
    } else {
      dateError.value = false
      notificationForm.value.date = x.datetime
    }
  })
}

const handleTranscriptsSelect = (report: Content[]) => {
  notificationForm.value.url = makeNotificationUrl(
    new Map([['id', report.map((x) => x.raw_id!)]])
  )
  report.map((x) => {
    notificationForm.value.title = getReportTitle(x) as string
    notificationForm.value.content = x.detail?.summary as string
    if (new Date(x.datetime) < new Date()) {
      dateError.value = true
      notify.error(t('error-notification-date'))
    } else {
      dateError.value = false
      notificationForm.value.date = x.datetime
    }
  })
}

defineExpose({
  isNotificationValid,
  sendNotification,
  sendOmniNotificationKv,
  setNotificationData,
  isNotificationChecked,
  setNotificationChecked,
  reset,
  notificationForm,
})
</script>

<template>
  <div class="form-section is-grey p-10">
    <div
      v-show="showNotificationCheck"
      class="field is-horizontal items-center"
    >
      <div class="field-label is-normal checkbox-title">
        <label class="label">{{ t('edit-form-has-notification') }}</label>
      </div>
      <div class="field-body">
        <V-Field>
          <V-Control>
            <label class="checkbox fieldset-checkbox is-outlined is-primary">
              <input v-model="isNotificationCheck" type="checkbox" />
              <span></span>
            </label>
          </V-Control>
        </V-Field>
      </div>
    </div>
    <base-form
      v-show="isNotificationCheck"
      v-model="notificationForm"
      class="max-w-540"
    >
      <select-content-type
        v-show="showTypeOption"
        v-model:type="notificationType"
        :options="optionsPushType"
        style="margin-left: 37px"
        @change="handleChangeType"
      />
      <form-field-select
        :options="optionsPlatformType"
        field="platform"
        :label="t('edit-form-platform')"
        :placeholder="t('placeholder-edit-form-platform')"
      />
      <form-field-input
        field="url"
        deep-watch
        :label="t('content-type-link')"
        :placeholder="t('editor-link-empty-placeholder')"
        icon="feather:link"
        :disabled="notificationType !== 'customize'"
      />
      <form-field-input
        field="title"
        deep-watch
        :label="t('form-title')"
        :placeholder="t('form-title-placeholder')"
        icon="feather:edit-2"
        required
      />
      <form-group
        direction="column"
        :style="!isMobileScreen && 'margin-left: 37px'"
      >
        <form-field-time
          :label="t('text-notification-date')"
          field="date"
          mode="date"
          class="push-date"
        />
        <form-field-time
          :label="t('text-notification-time')"
          :placeholder="t('placeholder-select-time')"
          field="date"
          mode="time"
          class="push-date"
          style="width: 39.8%; margin-top: -8px"
        />
      </form-group>
      <form-field-input
        multiline
        deep-watch
        field="content"
        :label="t('edit-form-notification-content')"
        :placeholder="t('edit-form-notification-content-placeholder')"
        icon="feather:edit-2"
      />
    </base-form>
    <ReportOmenPanel
      ref="searchOmenReportPanelComponent"
      :is-multi-select="false"
      @onSelectContents="handleOmenReportSelect"
    />
    <ArticlePanel
      ref="searchArticlePanelComponent"
      :is-multi-select="false"
      @onSelectContents="handleArticleSelect"
    />
    <VideoPanel
      ref="searchVideoPanelComponent"
      :is-multi-select="false"
      @onSelectContents="handleVideoSelect"
    />
    <RoadshowPanel
      ref="searchRoadshowPanelComponent"
      :is-multi-select="false"
      @onSelectContents="handleRoadshowSelect"
    />
    <ProductPanel
      ref="searchProductPanelComponent"
      :is-multi-select="false"
      @onSelectContents="handleProductSelect"
    />
    <PostPanel
      ref="searchPostPanelComponent"
      :is-multi-select="false"
      @onSelectContents="handlePostSelect"
    />
    <TranscriptsPanel
      ref="searchTranscriptsPanelComponent"
      :is-multi-select="false"
      @onSelectContents="handleTranscriptsSelect"
    />

  </div>
</template>

<style lang="scss" scoped>
.push-date {
  ::v-deep(.field-label) {
    white-space: nowrap;
  }
}
</style>
